// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateWishListSchedule from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListSchedule";
import translate from "@insite/client-framework/Translate";
import { WishListEmailScheduleModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import { formGridProps } from "@store/Themes/common/props/form";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

const largeGridItemProps: GridItemProps = {
    width: 12,
};

const smallGridItemProps: GridItemProps = {
    width: [12, 6, 6, 6, 6],
};

const dateGridItemProps: GridItemProps = {
    ...smallGridItemProps,
    css: css`
        flex-direction: column;
        align-items: initial;
    `,
};
/* Starter Custom Code - End */

interface OwnProps {
    wishList: WishListModel;
    isOpen: boolean;
    handleClose(): void;
    extendedStyles?: ScheduleReminderModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {
    updateWishListSchedule,
};

export interface ScheduleReminderModalStyles {
    modal?: ModalPresentationProps;
    headlineWraper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    hintText?: TypographyPresentationProps;
    contentContainer?: GridContainerProps;
    cancelConfirmationGridItem?: GridItemProps;
    cancelConfirmationText?: TypographyPresentationProps;
    cancelButtonsGridItem?: GridItemProps;
    dismissCancelButton?: ButtonPresentationProps;
    confirmCancelButton?: ButtonPresentationProps;
    topRowGridItem?: GridItemProps;
    frequencyRadioGroup?: RadioGroupProps;
    weeklyRadio?: RadioProps;
    monthlyRadio?: RadioProps;
    middleRowGridItem?: GridItemProps;
    middleRowContainer?: GridContainerProps;
    middleRowLeftColumnGridItem?: GridItemProps;
    leftColumnContainer?: GridContainerProps;
    intervalGridItem?: GridItemProps;
    intervalTextField?: TextFieldPresentationProps;
    dayGridItem?: GridItemProps;
    daySelect?: SelectPresentationProps;
    startDateGridItem?: GridItemProps;
    startDatePicker?: DatePickerPresentationProps;
    endDateGridItem?: GridItemProps;
    endDatePicker?: DatePickerPresentationProps;
    hasEndDateCheckbox?: CheckboxPresentationProps;
    middleRowRightColumnGridItem?: GridItemProps;
    messageTextArea?: TextAreaProps;
    bottomRowGridItem?: GridItemProps;
    buttonsContainer?: GridContainerProps;
    stopButtonGridItem?: GridItemProps;
    stopButton?: ButtonPresentationProps;
    cancelButtonGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    scheduleButtonGridItem?: GridItemProps;
    scheduleButton?: ButtonPresentationProps;
}

export const scheduleReminderModalStyles: ScheduleReminderModalStyles = {
    modal: {
        size: 625,
        cssOverrides: {
            modalBody: css`
                ${x => x.theme.modal.defaultProps?.cssOverrides?.modalBody}
                overflow-y: visible;
            `,
            modalContent: css`
                ${x => x.theme.modal.defaultProps?.cssOverrides?.modalContent}
                overflow-y: auto;

                ${breakpoint("sm")} {
                    overflow-y: visible;
                }
            `,
        },
    },
    hintText: {
        css: css`
            display: block;
            margin-bottom: ${pxToRem(20)};
        `,
    },
    contentContainer: formGridProps.gridContainer,
    cancelConfirmationGridItem: largeGridItemProps,
    dismissCancelButton: { variant: "secondary" },
    confirmCancelButton: { variant: "primary" },
    topRowGridItem: largeGridItemProps,
    frequencyRadioGroup: { horizontal: true },
    middleRowGridItem: largeGridItemProps,
    middleRowContainer: formGridProps.gridContainer,
    middleRowLeftColumnGridItem: largeGridItemProps,
    middleRowRightColumnGridItem: largeGridItemProps,
    leftColumnContainer: formGridProps.gridContainer,
    intervalGridItem: smallGridItemProps,
    dayGridItem: smallGridItemProps,
    startDateGridItem: dateGridItemProps,
    endDateGridItem: dateGridItemProps,
    stopButton: { variant: "secondary" },
    cancelButton: { variant: "secondary" },
    scheduleButton: { variant: "primary" },
};

const styles = scheduleReminderModalStyles;

const ScheduleReminderModal: React.FC<Props> = ({
    wishList,
    isOpen,
    handleClose,
    updateWishListSchedule,
    extendedStyles,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(scheduleReminderModalStyles, extendedStyles));

    /* Starter Custom Code - Start */
    const trackScheduleReminderWishList = ga.use.trackScheduleReminderWishList({}, { wishList });
    const trackRemoveReminderFromWishList = ga.use.trackRemoveReminderFromWishList({}, { wishList });
    /* Starter Custom Code - End */

    const { schedule, sendDayOfMonthPossibleValues, sendDayOfWeekPossibleValues } = wishList;

    const [repeatPeriod, setRepeatPeriod] = React.useState("Weekly");
    const repeatPeriodChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRepeatPeriod(event.currentTarget.value);
    };

    const [repeatInterval, setRepeatInterval] = React.useState<number | undefined>(1);
    const repeatIntervalChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.currentTarget.value) {
            setRepeatInterval(parseInt(event.currentTarget.value, 10));
        } else {
            setRepeatInterval(undefined);
        }
    };
    const repeatIntervalBlurHandler = () => {
        if (!repeatInterval || repeatInterval < 0) {
            setRepeatInterval(1);
        }
    };

    const [sendDayOfWeek, setSendDayOfWeek] = React.useState<{ key: string; value: string } | undefined>(undefined);
    const [sendDayOfMonth, setsendDayOfMonth] = React.useState<{ key: number; value: string } | undefined>(undefined);

    const sendDayChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (repeatPeriod === "Weekly") {
            setSendDayOfWeek(
                sendDayOfWeekPossibleValues?.find(o => o.key === event.currentTarget.value) ||
                    sendDayOfWeekPossibleValues?.[0],
            );
        } else {
            setsendDayOfMonth(
                sendDayOfMonthPossibleValues?.find(o => o.key.toString() === event.currentTarget.value) ||
                    sendDayOfMonthPossibleValues?.[0],
            );
        }
    };

    const [startDate, setStartDate] = React.useState<Date | undefined>(new Date());
    const startDateChangeHandler = ({ selectedDay }: DatePickerState) => {
        setStartDate(selectedDay || new Date());
        if (selectedDay && endDate && selectedDay > endDate) {
            setEndDate(undefined);
            setHasEndDate(false);
        }
    };

    const [hasEndDate, setHasEndDate] = React.useState(false);
    const hasEndDateChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        if (hasEndDate) {
            setEndDate(undefined);
            setHasEndDate(false);
        }
    };

    const [endDate, setEndDate] = React.useState<Date | undefined>(undefined);
    const endDateChangeHandler = ({ selectedDay }: DatePickerState) => {
        setEndDate(selectedDay);
        setHasEndDate(!!selectedDay);
    };

    const [message, setMessage] = React.useState("");
    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value.length > 300 ? event.target.value.slice(0, 300) : event.target.value);
    };

    const [inProgress, setInProgress] = React.useState(false);

    const scheduleClickHandler = () => {
        setInProgress(true);

        const scheduleToUpdate = wishList.schedule ? { ...wishList.schedule } : ({} as WishListEmailScheduleModel);
        scheduleToUpdate.repeatPeriod = repeatPeriod;
        scheduleToUpdate.repeatInterval = repeatInterval || 1;
        if (sendDayOfWeek) {
            scheduleToUpdate.sendDayOfWeek = sendDayOfWeek.key;
        }
        if (sendDayOfMonth) {
            scheduleToUpdate.sendDayOfMonth = sendDayOfMonth.key;
        }
        scheduleToUpdate.startDate = startDate as any;
        if (endDate) {
            scheduleToUpdate.endDate = endDate as any;
        } else {
            scheduleToUpdate.endDate = null;
        }
        scheduleToUpdate.message = message;

        updateWishListSchedule({
            wishListId: wishList.id,
            schedule: scheduleToUpdate,
            onSuccess: () => {
                handleClose();
                toasterContext.addToast({ body: siteMessage("Lists_Schedule_Saved"), messageType: "success" });
                /* Starter Custom Code - Start */
                if (!schedule) {
                    trackScheduleReminderWishList();
                }
                /* Starter Custom Code - End */
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const [cancelingReminder, setCancelingReminder] = React.useState(false);
    const stopClickHandler = () => {
        setCancelingReminder(true);
    };

    const dismissCancelClickHandler = () => {
        setCancelingReminder(false);
    };

    const confirmCancelClickHandler = () => {
        setInProgress(true);

        updateWishListSchedule({
            wishListId: wishList.id,
            schedule: null,
            onSuccess: () => {
                handleClose();
                toasterContext.addToast({ body: siteMessage("Lists_Schedule_Canceled"), messageType: "success" });
                trackRemoveReminderFromWishList(); // Starter Custom Code
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const resetFields = () => {
        setRepeatPeriod(schedule?.repeatPeriod || "Weekly");
        setRepeatInterval(schedule?.repeatInterval || 1);
        setSendDayOfWeek(
            sendDayOfWeekPossibleValues?.find(o => o.key === schedule?.sendDayOfWeek) ||
                sendDayOfWeekPossibleValues?.[0],
        );
        setsendDayOfMonth(
            sendDayOfMonthPossibleValues?.find(o => o.key === schedule?.sendDayOfMonth) ||
                sendDayOfMonthPossibleValues?.[0],
        );
        setStartDate(schedule?.startDate ? new Date(schedule.startDate.toString().split("T")[0]) : new Date());
        setEndDate(schedule?.endDate ? new Date(schedule.endDate.toString().split("T")[0]) : undefined);
        setHasEndDate(!!schedule?.endDate);
        setMessage(schedule?.message || "");
    };

    const afterCloseHandler = () => {
        setInProgress(false);
        setCancelingReminder(false);
        resetFields();
    };

    React.useEffect(() => {
        resetFields();
    }, [wishList]);

    const headline = (
        <StyledWrapper {...styles.headlineWraper}>
            <Typography {...styles.titleText}>
                {/* Starter Custom Code - Start */}
                {cancelingReminder ? translate("Stop Email Reminder") : translate("Schedule Email Reminder")}
                {/* Starter Custom Code - End */}
            </Typography>
            {/* Starter Moved - hintText - down */}
        </StyledWrapper>
    );

    const options =
        repeatPeriod === "Weekly"
            ? sendDayOfWeekPossibleValues?.map((pv: { key: string; value: string }) => (
                  <option key={pv.key}>{pv.value}</option>
              ))
            : sendDayOfMonthPossibleValues?.map((pv: { key: number; value: string }) => (
                  <option key={pv.key}>{pv.value}</option>
              ));

    return (
        <Modal
            {...styles.modal}
            headline={headline}
            isOpen={isOpen}
            handleClose={handleClose}
            onAfterClose={afterCloseHandler}
            data-test-selector="scheduleReminderModal"
        >
            {/* Starter Custom Code - Start */}
            {!cancelingReminder && (
                <Typography {...styles.hintText}>
                    {siteMessage(schedule ? "Lists_Schedule_Edit_Or_Cancel" : "Lists_Send_Reminders")}
                </Typography>
            )}
            {/* Starter Custom Code - End */}
            {cancelingReminder ? (
                <>
                    <GridContainer {...styles.contentContainer}>
                        <GridItem {...styles.cancelConfirmationGridItem}>
                            <Typography {...styles.cancelConfirmationText}>
                                {siteMessage("Lists_Schedule_Cancel_Confirmation")}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                    <FormActions /* Starter Custom Code */>
                        {/* Starter Moved - dismissCancelButton - down */}
                        <Button
                            {...styles.confirmCancelButton}
                            onClick={confirmCancelClickHandler}
                            disabled={inProgress}
                            data-test-selector="cancelingReminderConfirmButton"
                        >
                            {translate("Confirm")}
                        </Button>
                        <Button
                            {...styles.dismissCancelButton}
                            onClick={dismissCancelClickHandler}
                            data-test-selector="cancelingReminderCancelButton"
                        >
                            {translate("Cancel")}
                        </Button>
                    </FormActions>
                </>
            ) : (
                <>
                    <GridContainer {...styles.contentContainer}>
                        <GridItem {...styles.topRowGridItem}>
                            <RadioGroup
                                {...styles.frequencyRadioGroup}
                                label={translate("Frequency")}
                                value={repeatPeriod}
                                onChangeHandler={repeatPeriodChangeHandler}
                            >
                                <Radio {...styles.weeklyRadio} value="Weekly">
                                    {translate("Weekly")}
                                </Radio>
                                <Radio {...styles.monthlyRadio} value="Monthly">
                                    {translate("Monthly")}
                                </Radio>
                            </RadioGroup>
                        </GridItem>
                        <GridItem {...styles.middleRowGridItem}>
                            <GridContainer {...styles.middleRowContainer}>
                                <GridItem {...styles.middleRowLeftColumnGridItem}>
                                    <GridContainer {...styles.leftColumnContainer}>
                                        <GridItem {...styles.intervalGridItem}>
                                            <TextField
                                                {...styles.intervalTextField}
                                                label={translate("Interval")}
                                                type="number"
                                                min="1"
                                                value={repeatInterval}
                                                onChange={repeatIntervalChangeHandler}
                                                onBlur={repeatIntervalBlurHandler}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.dayGridItem}>
                                            <Select
                                                {...styles.daySelect}
                                                label={translate("Day")}
                                                value={
                                                    (repeatPeriod === "Weekly" ? sendDayOfWeek : sendDayOfMonth)?.key
                                                }
                                                onChange={sendDayChangeHandler}
                                            >
                                                {options}
                                            </Select>
                                        </GridItem>
                                        <GridItem {...styles.startDateGridItem}>
                                            <DatePicker
                                                {...styles.startDatePicker}
                                                label={translate("Start Date")}
                                                selectedDay={startDate}
                                                onDayChange={startDateChangeHandler}
                                                // Starter Custom Code
                                                dateTimePickerProps={{
                                                    clearIcon: null,
                                                }}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.endDateGridItem}>
                                            <DatePicker
                                                {...styles.endDatePicker}
                                                label={translate("End Date")}
                                                selectedDay={endDate}
                                                dateTimePickerProps={{
                                                    minDate: startDate,
                                                    clearIcon: null, // Starter Custom Code
                                                }}
                                                onDayChange={endDateChangeHandler}
                                            />
                                            {/* Starter Removed - hasEndDateCheckbox */}
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                <GridItem {...styles.middleRowRightColumnGridItem}>
                                    <TextArea
                                        {...styles.messageTextArea}
                                        label={translate("Message / Notes")}
                                        value={message}
                                        onChange={messageChangeHandler}
                                        hint={`${300 - message.length} ${translate("characters left")}`}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                    {/* Starter Custom Code - Start */}
                    <FormActions>
                        {schedule ? (
                            <Button
                                {...styles.stopButton}
                                onClick={stopClickHandler}
                                data-test-selector="btnStopReminder"
                            >
                                {translate("Stop Reminder")}
                            </Button>
                        ) : null}
                        <Button
                            {...styles.scheduleButton}
                            onClick={scheduleClickHandler}
                            disabled={inProgress}
                            data-test-selector="btnScheduleReminder"
                        >
                            {translate("Schedule")}
                        </Button>
                        {!schedule ? (
                            <Button
                                {...styles.cancelButton}
                                onClick={handleClose}
                                data-test-selector="btnCancelReminder"
                            >
                                {translate("Cancel")}
                            </Button>
                        ) : null}
                    </FormActions>
                    {/* Starter Custom Code - End */}
                </>
            )}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleReminderModal);
